import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuthToken, getUsuarioSistemaId } from '../utils/auth';
import { extractPostDate, formatPostDate, formatPostDateMinutes } from '../utils/dateUtils';
import Lightbox from '../components/Lightbox';
import './Funcionario.css';
import './Home.css';
import BASE_URL from '../utils/api';

// Helper para chamadas API com fallback de URLs
const apiFetch = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `/api/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];
  for (const url of urls) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const cfg = { method, headers };
      if (data && method !== 'GET') cfg.body = JSON.stringify(data);
      const res = await fetch(url, cfg);
      if (res.ok) {
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) return await res.json();
        return await res.text();
      }
    } catch (e) { continue; }
  }
  throw new Error(`Nenhuma URL funcionou para ${method} ${endpoint}`);
};

function Funcionario() {
  const location = useLocation();
  const navigate = useNavigate();
  const funcionario = location.state?.funcionario || null;
  const [dadosUsuario, setDadosUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conexaoLoading, setConexaoLoading] = useState(false);
  const [conexaoMsg, setConexaoMsg] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(false);
  const [fotoPerfil, setFotoPerfil] = useState(null);

  // ── Post feed state ──
  const F_PAGE_SIZE = 5;
  const [fPosts, setFPosts] = useState([]);
  const [fLoading, setFLoading] = useState(false);
  const [fSortOrder, setFSortOrder] = useState('desc');
  const [fHasMore, setFHasMore] = useState(true);
  const [fLoadingMore, setFLoadingMore] = useState(false);
  const [fLikedPosts, setFLikedPosts] = useState(new Set());
  const [fLikesCount, setFLikesCount] = useState({});
  const [fMyLikeId, setFMyLikeId] = useState({});
  const [fCommentsOpen, setFCommentsOpen] = useState(new Set());
  const [fCommentInputs, setFCommentInputs] = useState({});
  const [fCommentsByPost, setFCommentsByPost] = useState({});
  const [fCommentsCount, setFCommentsCount] = useState({});
  const [fUserNames, setFUserNames] = useState({});
  const [fImageIndexes, setFImageIndexes] = useState({});
  const [fLbOpen, setFLbOpen] = useState(false);
  const [fLbImages, setFLbImages] = useState([]);
  const [fLbIndex, setFLbIndex] = useState(0);

  const fSkipRef = useRef(0);
  const fHasMoreRef = useRef(true);
  const fLoadingMoreRef = useRef(false);
  const fPostObjectUrlRefs = useRef({});
  const fUserNamesFetching = useRef(new Set());
  const fBottomRef = useRef(null);

  useEffect(() => {
    if (!funcionario) { setLoading(false); return; }
    const fId = funcionario.id || funcionario.Id;
    const fetchDados = async () => {
      try {
        const data = await apiFetch(`DadosUsuario/por-usuario/${fId}`);
        setDadosUsuario(data);
      } catch (err) {
        console.error('Erro ao buscar dados do funcionário', err);
      } finally { setLoading(false); }
    };
    fetchDados();
    // Buscar foto de perfil
    const fetchFoto = async () => {
      try {
        const token = getAuthToken();
        const bases = [BASE_URL.replace(/\/api\/?$/, '')];
        for (const base of bases) {
          try {
            const res = await fetch(`${base}/api/DadosUsuario/foto-por-usuario/${fId}`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (res.ok) {
              const blob = await res.blob();
              if (blob.size > 0) {
                setFotoPerfil(URL.createObjectURL(blob));
                return;
              }
            }
            if (res.status === 404) { setFotoPerfil(null); return; }
          } catch (e) { continue; }
        }
        setFotoPerfil(null);
      } catch (e) { setFotoPerfil(null); }
    };
    fetchFoto();
  }, [funcionario]);

  useEffect(() => {
    const checkConnection = async () => {
      const origemId = getUsuarioSistemaId();
      const destinoId = funcionario?.id || funcionario?.Id;
      if (!origemId || !destinoId) return;
      try {
        const conexoes = await apiFetch(`ConexaoUsuario/origem/${origemId}`);
        const arr = Array.isArray(conexoes) ? conexoes : [];
        const exists = arr.some(c => {
          const dest = c.UsuarioIdDestino || c.usuarioIdDestino;
          return parseInt(dest, 10) === parseInt(destinoId, 10);
        });
        setIsConnected(exists);
      } catch (err) {
        setIsConnected(false);
      }
    };
    if (funcionario) checkConnection();
  }, [funcionario]);

  // ── Post feed helpers ──

  const fFetchFileBlobUrl = useCallback(async (fileId) => {
    const token = getAuthToken();
    const bases = [BASE_URL.replace(/\/api\/?$/, '')];
    for (const base of bases) {
      try {
        const url = `${base}/api/PostagemArquivo/${fileId}`;
        const res = await fetch(url, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
        if (res.ok) {
          const blob = await res.blob();
          if (blob.size > 0) return URL.createObjectURL(blob);
        }
      } catch (e) { continue; }
    }
    return null;
  }, []);

  const fFetchFilesForPost = useCallback(async (p) => {
    try {
      const pid = p.Id || p.id || p.PostagemId || p.postagemId;
      if (!pid) return p;
      const fa = await apiFetch(`PostagemArquivo/postagem/${pid}`);
      const arrFiles = Array.isArray(fa) ? fa : [];
      let files = (await Promise.all((arrFiles || []).map(async f => {
        const type = f.TipoArquivo || f.tipoArquivo || f.tipo || 'application/octet-stream';
        let url = null;
        if (typeof f.Arquivo === 'string' && f.Arquivo.trim().length > 0) {
          url = `data:${type};base64,` + f.Arquivo;
        } else if (Array.isArray(f.Arquivo) && f.Arquivo.length) {
          const b = Uint8Array.from(f.Arquivo);
          url = URL.createObjectURL(new Blob([b], { type }));
          try { fPostObjectUrlRefs.current[pid] = fPostObjectUrlRefs.current[pid] || []; fPostObjectUrlRefs.current[pid].push(url); } catch (e) {}
        }
        if (!url && (f.Id || f.id)) {
          try { url = await fFetchFileBlobUrl(f.Id || f.id); } catch (e) { url = null; }
        }
        const fileName = f.NomeArquivo || f.nomeArquivo || f.Nome || f.nome || f.fileName || f.file || f.name || '';
        return { id: f.Id || f.id || null, type, url, name: fileName, NomeArquivo: f.NomeArquivo || f.nomeArquivo || f.Nome || f.nome };
      }))).filter(Boolean);

      const detectOrientation = (url) => new Promise(resolve => {
        try {
          const img = new Image();
          img.onload = () => {
            const w = img.naturalWidth, h = img.naturalHeight;
            const ratio = Math.abs(h - w) / Math.max(h, w, 1);
            const isSquare = ratio < 0.05;
            resolve({ isPortrait: !isSquare && h > w, isSquare });
          };
          img.onerror = () => resolve({ isPortrait: false, isSquare: false });
          img.src = url;
        } catch (e) { resolve({ isPortrait: false, isSquare: false }); }
      });

      files = await Promise.all(files.map(async (f) => {
        if (f.type && f.type.startsWith('image/') && f.url) {
          try { const o = await detectOrientation(f.url); f.isPortrait = o.isPortrait; f.isSquare = o.isSquare; }
          catch (e) { f.isPortrait = false; f.isSquare = false; }
        } else { f.isPortrait = false; f.isSquare = false; }
        return f;
      }));
      return { ...p, files };
    } catch (e) { return { ...p, files: [] }; }
  }, [fFetchFileBlobUrl]);

  const fFetchCounts = useCallback(async (postsArray) => {
    if (!postsArray || postsArray.length === 0) return;
    await Promise.all(postsArray.map(async (mp) => {
      const pid = mp.Id || mp.id || mp.PostagemId || mp.postagemId;
      if (!pid) return;
      try {
        const likeRes = await apiFetch(`PostagemCurtida/postagem/${pid}`);
        if (likeRes && typeof likeRes.count === 'number') setFLikesCount(prev => ({ ...prev, [pid]: likeRes.count }));
        else if (Array.isArray(likeRes)) setFLikesCount(prev => ({ ...prev, [pid]: likeRes.length }));
      } catch (e) { setFLikesCount(prev => ({ ...prev, [pid]: 0 })); }
      try {
        const comRes = await apiFetch(`PostagemComentario/postagem/${pid}`);
        if (Array.isArray(comRes)) setFCommentsCount(prev => ({ ...prev, [pid]: comRes.length }));
        else if (comRes && typeof comRes.count === 'number') setFCommentsCount(prev => ({ ...prev, [pid]: comRes.count }));
      } catch (e) { setFCommentsCount(prev => ({ ...prev, [pid]: 0 })); }
    }));
  }, []);

  const fFetchUserName = useCallback(async (userId) => {
    if (!userId || fUserNames[userId] || fUserNamesFetching.current.has(userId)) return;
    fUserNamesFetching.current.add(userId);
    try {
      const data = await apiFetch(`DadosUsuario/por-usuario/${userId}`);
      const nome = data?.NomeUsuario || data?.nomeUsuario || data?.Nome || data?.nome || null;
      if (nome) setFUserNames(prev => ({ ...prev, [userId]: nome }));
    } catch (e) {} finally { fUserNamesFetching.current.delete(userId); }
  }, [fUserNames]);

  const loadFPosts = useCallback(async ({ reset = false } = {}) => {
    const userId = funcionario?.id || funcionario?.Id;
    if (!userId) return;
    if (fLoadingMoreRef.current) return;

    if (reset) {
      setFPosts([]);
      setFHasMore(true);
      fSkipRef.current = 0;
      fHasMoreRef.current = true;
    }

    setFLoading(prev => reset ? true : prev);
    setFLoadingMore(true);
    fLoadingMoreRef.current = true;

    try {
      const baseSkip = reset ? 0 : fSkipRef.current;
      const endpoint = fSortOrder === 'asc'
        ? `Postagem/usuario/${userId}/antigo?skip=${baseSkip}&take=${F_PAGE_SIZE}`
        : `Postagem/usuario/${userId}?skip=${baseSkip}&take=${F_PAGE_SIZE}`;
      const data = await apiFetch(endpoint);
      const arr = (data && data.Items) ? data.Items : (Array.isArray(data) ? data : (data?.items || []));
      const total = data && typeof data.Total === 'number' ? data.Total : null;

      const withDate = arr.map(p => ({ ...p, createdAt: extractPostDate(p) }));
      const enriched = await Promise.all(withDate.map(pp => fFetchFilesForPost(pp)));

      if (reset) {
        setFPosts(enriched);
      } else {
        setFPosts(prev => {
          const ids = new Set(prev.map(x => x.Id || x.id));
          const add = enriched.filter(x => { const id = x.Id || x.id; return id && !ids.has(id); });
          return [...prev, ...add];
        });
      }

      const newSkip = baseSkip + arr.length;
      fSkipRef.current = newSkip;
      const hm = total !== null ? newSkip < total : arr.length === F_PAGE_SIZE;
      setFHasMore(hm);
      fHasMoreRef.current = hm;

      fFetchCounts(enriched).catch(() => {});
    } catch (e) {
      console.error('Erro ao carregar postagens do funcionário', e);
    } finally {
      setFLoading(false);
      setFLoadingMore(false);
      fLoadingMoreRef.current = false;
    }
  }, [funcionario, fSortOrder, fFetchFilesForPost, fFetchCounts]);

  // Load posts when funcionario or sort order changes
  useEffect(() => {
    if (funcionario) loadFPosts({ reset: true });
    return () => {
      try { Object.values(fPostObjectUrlRefs.current || {}).forEach(arr => (arr || []).forEach(u => { try { URL.revokeObjectURL(u); } catch {} })); } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funcionario, fSortOrder]);

  // Infinite scroll sentinel
  useEffect(() => {
    const el = fBottomRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && fHasMoreRef.current && !fLoadingMoreRef.current) {
          loadFPosts({ reset: false }).catch(() => {});
        }
      });
    }, { root: null, rootMargin: '300px', threshold: 0 });
    obs.observe(el);
    return () => { try { obs.disconnect(); } catch (e) {} };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fLoading]);

  // Sorted posts (filter is done server-side, just sort client copy)
  const fSortedPosts = useMemo(() => {
    const copy = fPosts.slice();
    copy.sort((a, b) => {
      const da = new Date(a.createdAt || 0);
      const db = new Date(b.createdAt || 0);
      return fSortOrder === 'asc' ? da - db : db - da;
    });
    return copy;
  }, [fPosts, fSortOrder]);

  // Like toggle
  const fToggleLike = async (p) => {
    const pid = p.Id || p.id || p.PostagemId || p.postagemId;
    if (!pid) return;
    const isLiked = fLikedPosts.has(pid);
    setFLikedPosts(prev => { const s = new Set(prev); isLiked ? s.delete(pid) : s.add(pid); return s; });
    setFLikesCount(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) + (isLiked ? -1 : 1)) }));
    if (!isLiked) {
      try {
        const created = await apiFetch('PostagemCurtida', 'POST', { PostagemId: pid });
        const createdId = created?.Id || created?.id || null;
        if (createdId) setFMyLikeId(prev => ({ ...prev, [pid]: createdId }));
      } catch (e) {
        setFLikedPosts(prev => { const s = new Set(prev); s.delete(pid); return s; });
        setFLikesCount(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 1) - 1) }));
      }
    } else {
      const likeId = fMyLikeId[pid];
      if (likeId) {
        try { await apiFetch(`PostagemCurtida/${likeId}`, 'DELETE'); setFMyLikeId(prev => { const c = { ...prev }; delete c[pid]; return c; }); }
        catch (e) { setFLikedPosts(prev => { const s = new Set(prev); s.add(pid); return s; }); setFLikesCount(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 })); }
      }
    }
  };

  // Toggle comments
  const fToggleComments = async (p) => {
    const pid = p.Id || p.id;
    setFCommentsOpen(prev => { const s = new Set(prev); s.has(pid) ? s.delete(pid) : s.add(pid); return s; });
    if (!fCommentsByPost[pid]) {
      try {
        const res = await apiFetch(`PostagemComentario/postagem/${pid}`);
        const arr = Array.isArray(res) ? res : [];
        setFCommentsByPost(prev => ({ ...prev, [pid]: arr }));
        setFCommentsCount(prev => ({ ...prev, [pid]: arr.length }));
      } catch (e) {}
    }
  };

  const fSubmitComment = async (pid) => {
    const text = (fCommentInputs[pid] || '').trim();
    if (!text) return;
    try {
      const res = await apiFetch('PostagemComentario', 'POST', { PostagemId: pid, Conteudo: text });
      setFCommentsByPost(prev => ({ ...prev, [pid]: [...(prev[pid] || []), res] }));
      setFCommentsCount(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
      setFCommentInputs(prev => ({ ...prev, [pid]: '' }));
    } catch (e) {}
  };

  const handleCriarConexao = async () => {
    const origemId = getUsuarioSistemaId();
    const destinoId = funcionario?.id || funcionario?.Id;
    if (!origemId || !destinoId) { setConexaoMsg('Erro: IDs não encontrados.'); return; }
    setConexaoLoading(true);
    setConexaoMsg('');
    try {
      await apiFetch('ConexaoUsuario', 'POST', {
        UsuarioIdOrigem: origemId,
        UsuarioIdDestino: destinoId
      });
      // Recheck connection after POST
      const conexoes = await apiFetch(`ConexaoUsuario/origem/${origemId}`);
      const arr = Array.isArray(conexoes) ? conexoes : [];
      const exists = arr.some(c => {
        const dest = c.UsuarioIdDestino || c.usuarioIdDestino;
        return parseInt(dest, 10) === parseInt(destinoId, 10);
      });
      setIsConnected(exists);
      setConexaoMsg('Conexão criada com sucesso!');
      setTimeout(() => setConexaoMsg(''), 3000);
    } catch (err) {
      console.error('Erro ao criar conexão', err);
      setConexaoMsg('Erro ao criar conexão: ' + (err.message || err));
    } finally { setConexaoLoading(false); }
  };

  const handleDesconectar = async () => {
    const origemId = getUsuarioSistemaId();
    const destinoId = funcionario?.id || funcionario?.Id;
    if (!origemId || !destinoId) return;
    setConexaoLoading(true);
    setConexaoMsg('');
    setShowConfirmDisconnect(false);
    try {
      // Try both directions for DELETE
      try {
        await apiFetch(`ConexaoUsuario/${origemId}/${destinoId}`, 'DELETE');
      } catch {
        await apiFetch(`ConexaoUsuario/${destinoId}/${origemId}`, 'DELETE');
      }
      setIsConnected(false);
      setConexaoMsg('Conexão desfeita!');
      setTimeout(() => setConexaoMsg(''), 3000);
    } catch (err) {
      console.error('Erro ao desfazer conexão', err);
      setConexaoMsg('Erro ao desfazer conexão: ' + (err.message || err));
    } finally { setConexaoLoading(false); }
  };

  const handleConnectionClick = () => {
    if (isConnected) {
      setShowConfirmDisconnect(true);
    } else {
      handleCriarConexao();
    }
  };

  const handleEnviarMensagem = () => {
    // Signal ChatWidget to open with this person
    const pessoa = {
      id: funcionario?.id || funcionario?.Id,
      nome: nomeCompleto,
      login: login
    };
    localStorage.setItem('openChatWithPerson', JSON.stringify(pessoa));
    window.dispatchEvent(new Event('openChatWithPerson'));
    navigate('/sidebar/Home'); // Navigate to Home where ChatWidget is
  };

  if (!funcionario) {
    return (
      <div className="funcionario-page">
        <div className="funcionario-top-bar">
          <button className="funcionario-btn-voltar" onClick={() => navigate('/sidebar/Home')}>← Voltar</button>
        </div>
        <div className="funcionario-card-container">
          <p style={{ color: '#999', textAlign: 'center' }}>Nenhum funcionário selecionado.</p>
        </div>
      </div>
    );
  }

  const nomeCompleto = dadosUsuario?.nomeUsuario || dadosUsuario?.NomeUsuario || dadosUsuario?.Nome || funcionario.login || funcionario.Login || 'Funcionário';
  const login = funcionario.login || funcionario.Login || '';

  return (
    <div className="funcionario-page">
      {/* ── Voltar button top-left ── */}
      <div className="funcionario-top-bar">
        <button className="funcionario-btn-voltar" onClick={() => navigate(-1)}>← Voltar</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Carregando...</div>
      ) : (
        <>
          <div className="funcionario-card-container">
            {/* Circulo grande à esquerda + info à direita (estilo Grupo) */}
            <div className="funcionario-avatar">
              {fotoPerfil ? (
                <img src={fotoPerfil} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              ) : (
                <svg width="90" height="90" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" fill="#fff3e0" stroke="rgb(235, 133, 0)" strokeWidth="1.5"/>
                  <circle cx="12" cy="9" r="3.5" fill="rgb(235, 133, 0)"/>
                  <path d="M5.5 19.5c0-3.5 3-5.5 6.5-5.5s6.5 2 6.5 5.5" stroke="rgb(235, 133, 0)" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
            </div>
            <div className="funcionario-info">
              <div className="funcionario-nome">{nomeCompleto}</div>
              <div className="funcionario-login">{login}</div>
              {/* Botões de ação dentro do card */}
              <div className="funcionario-actions-inline">
                <button 
                  className={isConnected ? "funcionario-btn-connected" : "funcionario-btn-conexao"} 
                  onClick={handleConnectionClick} 
                  disabled={conexaoLoading}
                >
                  {conexaoLoading ? '...' : (isConnected ? 'Conectado' : 'Criar conexão')}
                </button>
                <button className="funcionario-btn-mensagem" onClick={handleEnviarMensagem}>
                  Enviar mensagem
                </button>
              </div>
            </div>
          </div>

          {conexaoMsg && (
            <div style={{ marginTop: 12, textAlign: 'center', color: conexaoMsg.includes('sucesso') || conexaoMsg.includes('desfeita') ? '#4caf50' : '#d32f2f', fontWeight: 600, fontSize: 14 }}>
              {conexaoMsg}
            </div>
          )}

          {/* ── Post feed section ── */}
          <div className="funcionario-feed-section">
            <div className="funcionario-feed-header">
              <h3 className="funcionario-feed-title">
                Publicações públicas {fLoading && fSortedPosts.length === 0 ? '(Carregando...)' : `(${fSortedPosts.length})`}
              </h3>
              <div className="funcionario-sort-bar">
                <label htmlFor="f-sort" style={{ fontSize: 13, color: '#666', marginRight: 6 }}>Ordenar:</label>
                <select
                  id="f-sort"
                  className="funcionario-sort-select"
                  value={fSortOrder}
                  onChange={e => setFSortOrder(e.target.value)}
                >
                  <option value="desc">Mais recente</option>
                  <option value="asc">Mais antigo</option>
                </select>
              </div>
            </div>

            {!fLoading && fSortedPosts.length === 0 && (
              <div style={{ color: '#999', textAlign: 'center', padding: '24px 0', fontSize: 14 }}>
                Nenhuma publicação pública encontrada.
              </div>
            )}

            <div className="post-list">
              {fSortedPosts.map((p, idx) => {
                const pid = p.Id || p.id || p.PostagemId || p.postagemId;
                const postUserId = p.UsuarioSistemaId || p.usuarioSistemaId;
                if (postUserId && !fUserNames[postUserId] && !fUserNamesFetching.current.has(postUserId)) {
                  fFetchUserName(postUserId);
                }
                const postUserName = fUserNames[postUserId] || null;
                const imgIdx = fImageIndexes[pid] || 0;
                const images = (p.files || []).filter(f => f && f.type && (f.type.startsWith('image/') || f.type.startsWith('video/')));
                const nonImages = (p.files || []).filter(f => f && f.type && !f.type.startsWith('image/') && !f.type.startsWith('video/'));
                const cf = images[imgIdx] || {};

                const fileEmoji = (f) => {
                  const t = (f.type || '').toLowerCase();
                  const n = (f.name || f.fileName || f.Nome || f.nome || '').toLowerCase();
                  if (t.includes('pdf') || n.endsWith('.pdf')) return '📄';
                  if (t.includes('text') || n.endsWith('.txt')) return '📝';
                  if (n.endsWith('.csv')) return '📑';
                  return '📎';
                };

                return (
                  <div className="post-card" key={pid || idx} style={{ position: 'relative' }}>
                    {/* Author + date */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                      {postUserName && <div className="post-author" style={{ fontWeight: 'bold', color: '#333' }}>{postUserName}</div>}
                      <div className="post-time">{formatPostDate(p.createdAt)}</div>
                    </div>
                    {(p.Titulo || p.title || p.titulo) && <div className="post-title">{p.Titulo || p.title || p.titulo}</div>}
                    {(p.Descricao || p.descricao || p.description) && (
                      <div className="post-description">{p.Descricao || p.descricao || p.description}</div>
                    )}

                    {/* Image gallery */}
                    {images.length > 0 && (
                      <div>
                        <div className={`post-image-wrap ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`}>
                          {images.length > 1 && (
                            <div className="post-image-arrow left" role="button" title="Anterior"
                              onClick={() => setFImageIndexes(prev => ({ ...prev, [pid]: (imgIdx - 1 + images.length) % images.length }))}>◀</div>
                          )}
                          {cf && cf.url ? (
                            cf.type && cf.type.startsWith('video/') ? (
                              <div className="post-video-wrap"><video src={cf.url} className={`post-video ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`} controls /></div>
                            ) : (
                              <img
                                className={`post-main-image ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`}
                                src={cf.url} alt="Post image" loading="lazy"
                                onClick={() => { setFLbImages(images.map(f => ({ url: f.url, type: f.type }))); setFLbIndex(imgIdx); setFLbOpen(true); }}
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            )
                          ) : null}
                          {images.length > 1 && (
                            <div className="post-image-arrow right" role="button" title="Próxima"
                              onClick={() => setFImageIndexes(prev => ({ ...prev, [pid]: (imgIdx + 1) % images.length }))}>▶</div>
                          )}
                          {images.length > 1 && <div className="post-image-count">{imgIdx + 1} / {images.length}</div>}
                        </div>
                        {images.length > 1 && (
                          <div className="post-thumbs">
                            {images.map((f, ti) => (
                              <div key={ti} className={`post-thumb ${imgIdx === ti ? 'active' : ''}`}
                                onClick={() => setFImageIndexes(prev => ({ ...prev, [pid]: ti }))}>
                                {f.type && f.type.startsWith('video/') ? (
                                  <video src={f.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <img src={f.url} alt={`thumb-${ti}`} loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Non-image files */}
                    {nonImages.length > 0 && (
                      <div className="files-preview" style={{ marginTop: 12 }}>
                        <div className="files-row files-row-scrollable">
                          {nonImages.map((f, fi) => {
                            const fname = f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name || `Arquivo ${fi + 1}`;
                            return (
                              <div key={f.id || fi} className="file-item">
                                <div style={{ fontSize: 18 }}>{fileEmoji(f)}</div>
                                <a className="file-title" href={f.url} download={fname} target="_blank" rel="noopener noreferrer">{fname}</a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Likes / Comments counts */}
                    <div style={{ display: 'flex', gap: 12, marginTop: 10, marginBottom: 4, fontSize: 13, color: '#888' }}>
                      {(fLikesCount[pid] || 0) > 0 && <span>{fLikesCount[pid]} curtida{(fLikesCount[pid] || 0) !== 1 ? 's' : ''}</span>}
                      {(fCommentsCount[pid] || 0) > 0 && <span>{fCommentsCount[pid]} comentário{(fCommentsCount[pid] || 0) !== 1 ? 's' : ''}</span>}
                    </div>

                    {/* Action buttons */}
                    <div className="post-actions">
                      <button type="button" className={`action-btn like-btn ${fLikedPosts.has(pid) ? 'liked' : ''}`}
                        onClick={() => fToggleLike(p)} aria-pressed={fLikedPosts.has(pid)}>
                        <svg className="icon heart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        <span>Curtir</span>
                      </button>
                      <button type="button" className={`action-btn comments-btn ${fCommentsOpen.has(pid) ? 'open' : ''}`}
                        onClick={() => fToggleComments(p)}>
                        <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        <span>Comentários</span>
                      </button>
                    </div>

                    {/* Comments section */}
                    {fCommentsOpen.has(pid) && (
                      <div className="comments-section">
                        <div className="comment-input-row">
                          <input className="comment-input" type="text" placeholder="Escreva um comentário..."
                            value={fCommentInputs[pid] || ''}
                            onChange={e => setFCommentInputs(prev => ({ ...prev, [pid]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); fSubmitComment(pid); } }} />
                          <button type="button" className="comment-button" onClick={() => fSubmitComment(pid)}>Comentar</button>
                        </div>
                        {(fCommentsByPost[pid] || []).length > 0 && (
                          <div className="comment-list">
                            {(fCommentsByPost[pid] || []).map(c => {
                              const cUserId = c.UsuarioSistemaId || c.usuarioSistemaId;
                              if (cUserId && !fUserNames[cUserId]) fFetchUserName(cUserId);
                              const cName = fUserNames[cUserId] || 'Usuário';
                              return (
                                <div key={c.id || c.Id} className="comment-item">
                                  <div className="comment-header"><strong>{cName}</strong><span className="comment-time">{c.DataHora ? formatPostDateMinutes(c.DataHora) : ''}</span></div>
                                  <div className="comment-text">{c.conteudo || c.text || c.Conteudo}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={fBottomRef} style={{ height: 1 }} />
            <div style={{ textAlign: 'center', padding: 12, color: '#666' }}>
              {fLoading && fSortedPosts.length === 0 ? 'Carregando publicações...' : (
                fLoadingMore ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <svg width="26" height="26" viewBox="0 0 50 50" style={{ animation: 'funcionarioSpin 1s linear infinite' }}>
                      <circle cx="25" cy="25" r="20" stroke="rgb(235, 133, 0)" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                    </svg>
                    <span style={{ fontSize: 13 }}>Carregando mais...</span>
                  </div>
                ) : (!fHasMore && fSortedPosts.length > 0 ? <span style={{ fontSize: 13 }}>Não há mais publicações.</span> : '')
              )}
              <style>{`@keyframes funcionarioSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          </div>
        </>
      )}

      {/* Popup de confirmação para desfazer conexão */}
      {showConfirmDisconnect && (
        <div className="funcionario-confirm-overlay" onClick={() => setShowConfirmDisconnect(false)}>
          <div className="funcionario-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <p>Tem certeza que deseja desfazer a conexão?</p>
            <div className="funcionario-confirm-buttons">
              <button className="funcionario-confirm-yes" onClick={handleDesconectar}>Sim</button>
              <button className="funcionario-confirm-no" onClick={() => setShowConfirmDisconnect(false)}>Não</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {fLbOpen && (
        <Lightbox
          images={fLbImages}
          index={fLbIndex}
          onClose={() => setFLbOpen(false)}
          onNext={() => setFLbIndex(i => (i + 1) % Math.max(1, fLbImages.length))}
          onPrev={() => setFLbIndex(i => (i - 1 + Math.max(1, fLbImages.length)) % Math.max(1, fLbImages.length))}
        />
      )}
    </div>
  );
}

export default Funcionario;
